/**
 * WorkbenchSlicePreview — suggest_slice 결과 미리보기 카드.
 *
 * suggest_slice 도구가 반환한 슬라이스 프리뷰(kinds · relations · shapes)를
 * 컴팩트한 카드로 렌더하고, "그래프에 적용" 버튼으로 상위에 알린다.
 *
 * PlaygroundApp 의 ExtractPreview 패턴을 재구현(직접 import 금지) — 데이터는
 * WorkbenchChat 에서 suggest_slice output 의 preview 를 그대로 받는다.
 */

/** suggest_slice preview 타입 — kinds · relations · shapes 배열. */
export interface SlicePreview {
	readonly kinds: ReadonlyArray<{ readonly name?: string; readonly [key: string]: unknown }>
	readonly relations: ReadonlyArray<{
		readonly name?: string
		readonly displayName?: string
		readonly [key: string]: unknown
	}>
	readonly shapes: ReadonlyArray<{
		readonly sourceKind?: string
		readonly relation?: string
		readonly targetKind?: string
		readonly [key: string]: unknown
	}>
}

export interface WorkbenchSlicePreviewProps {
	/** suggest_slice 도구 output 의 preview 필드. */
	readonly preview: SlicePreview
	/** "그래프에 적용" 버튼 클릭 콜백. */
	readonly onApply: () => void
	/** 닫기/무시 버튼 클릭 콜백 (선택적). */
	readonly onDiscard?: () => void
}

/**
 * WorkbenchSlicePreview — kinds N · relations M · shapes K 요약 카드.
 *
 * - kind 칩 목록
 * - relation 칩 목록
 * - shape flow 목록 (sourceKind → relation → targetKind)
 * - 하단: "그래프에 적용" / "무시" 버튼
 */
export function WorkbenchSlicePreview({ preview, onApply, onDiscard }: WorkbenchSlicePreviewProps) {
	const { kinds, relations, shapes } = preview

	return (
		<section className="workbench-slice-preview" aria-label="슬라이스 미리보기">
			<div className="workbench-slice-preview__header">
				<span className="workbench-slice-preview__title">슬라이스 미리보기</span>
				<span className="workbench-slice-preview__counts" aria-label="슬라이스 구성 요소">
					<span className="workbench-slice-preview__count-item">
						kinds <strong>{kinds.length}</strong>
					</span>
					<span className="workbench-slice-preview__count-sep" aria-hidden>
						·
					</span>
					<span className="workbench-slice-preview__count-item">
						relations <strong>{relations.length}</strong>
					</span>
					<span className="workbench-slice-preview__count-sep" aria-hidden>
						·
					</span>
					<span className="workbench-slice-preview__count-item">
						shapes <strong>{shapes.length}</strong>
					</span>
				</span>
			</div>

			{kinds.length > 0 && (
				<div className="workbench-slice-preview__section">
					<div className="workbench-slice-preview__section-label">종류</div>
					<ul className="workbench-slice-preview__chip-list">
						{kinds.map((k) => (
							<li
								key={typeof k.name === "string" ? k.name : String(k.name)}
								className="chip workbench-slice-preview__chip"
								title={typeof k.name === "string" ? k.name : undefined}
							>
								{k.name ?? "—"}
							</li>
						))}
					</ul>
				</div>
			)}

			{relations.length > 0 && (
				<div className="workbench-slice-preview__section">
					<div className="workbench-slice-preview__section-label">관계</div>
					<ul className="workbench-slice-preview__chip-list">
						{relations.map((r) => {
							const rKey = r.name ?? r.displayName ?? ""
							return (
								<li
									key={rKey}
									className="chip workbench-slice-preview__chip workbench-slice-preview__chip--relation"
									title={r.name ?? r.displayName ?? undefined}
								>
									{r.name ?? r.displayName ?? "—"}
								</li>
							)
						})}
					</ul>
				</div>
			)}

			{shapes.length > 0 && (
				<div className="workbench-slice-preview__section">
					<div className="workbench-slice-preview__section-label">형태</div>
					<ul className="workbench-slice-preview__shape-list">
						{shapes.map((s, i) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: 순서 고정 배열
							<li key={i} className="workbench-slice-preview__shape-row">
								<span className="workbench-slice-preview__shape-kind">{s.sourceKind ?? "?"}</span>
								<span className="workbench-slice-preview__shape-arrow" aria-hidden>
									→
								</span>
								<span className="workbench-slice-preview__shape-rel">{s.relation ?? "?"}</span>
								<span className="workbench-slice-preview__shape-arrow" aria-hidden>
									→
								</span>
								<span className="workbench-slice-preview__shape-kind">{s.targetKind ?? "?"}</span>
							</li>
						))}
					</ul>
				</div>
			)}

			<div className="workbench-slice-preview__actions">
				<span className="workbench-slice-preview__applied-badge" aria-live="polite">
					그래프에 반영됨
				</span>
				<button type="button" className="btn btn-ghost btn-sm" onClick={onApply}>
					닫기
				</button>
				{onDiscard && (
					<button type="button" className="btn btn-ghost btn-sm" onClick={onDiscard}>
						무시
					</button>
				)}
			</div>
		</section>
	)
}
